import React from 'react';
import { ChevronDown, ChevronRight, CheckCircle2, Circle } from 'lucide-react';
import { Section, Progress } from '../types';

interface OutlinePanelProps {
  sections: Section[];
  currentSection?: string;
  progress?: Progress[];
  onSelectSection: (sectionId: string) => void;
}

export default function OutlinePanel({ 
  sections, 
  currentSection, 
  progress = [], 
  onSelectSection 
}: OutlinePanelProps) {
  const [expandedSections, setExpandedSections] = React.useState<Set<string>>(
    new Set(sections.map(s => s.id))
  );

  const toggleSection = (sectionId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  };

  const isCompleted = (sectionId: string) => {
    return progress.some(p => p.section_id === sectionId && p.completed);
  };

  const getProgressPercentage = () => {
    if (!progress.length) return 0;
    const sectionIds = new Set(sections.map(section => section.id));
    const totalSections = sections.reduce(
      (acc, s) => acc + (s.subsections.length > 0 ? s.subsections.length : 1),
      0
    );
    if (totalSections === 0) return 0;
    const completedCount = progress.filter(p => p.completed && sectionIds.has(p.section_id)).length;
    return Math.round((completedCount / totalSections) * 100);
  };

  return (
    <div className="h-full bg-apple-card border-r border-apple-border flex flex-col">
      {/* Header */}
      <div className="px-4 py-4 border-b border-apple-border">
        <h2 className="font-semibold text-apple-text mb-2">Course Outline</h2>
        <div className="flex items-center gap-2">
          <div className="flex-1 h-2 bg-apple-gray rounded-full overflow-hidden">
            <div 
              className="h-full bg-apple-blue transition-all duration-500"
              style={{ width: `${getProgressPercentage()}%` }}
            />
          </div>
          <span className="text-xs text-apple-text-secondary">{getProgressPercentage()}%</span>
        </div>
      </div>

      {/* Sections */}
      <div className="flex-1 overflow-y-auto py-2">
        {sections.map((section) => (
          <div key={section.id} className="mb-1">
            {/* Section Header */}
            <div
              className={`w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-apple-gray transition-colors cursor-pointer ${
                currentSection === section.id || section.subsections.some(sub => sub.id === currentSection) ? 'bg-blue-50' : ''
              }`}
            >
              <button
                onClick={(e) => toggleSection(section.id, e)}
                className="p-1 hover:bg-gray-200 rounded"
                disabled={section.subsections.length === 0}
              >
                {section.subsections.length === 0 ? (
                  <Circle className="w-4 h-4 text-apple-text-secondary" />
                ) : expandedSections.has(section.id) ? (
                  <ChevronDown className="w-4 h-4 text-apple-text-secondary" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-apple-text-secondary" />
                )}
              </button>
              <span 
                className="font-medium text-apple-text text-sm flex-1"
                onClick={() => {
                  onSelectSection(section.subsections[0]?.id || section.id);
                }}
              >
                {section.title}
              </span>
            </div>

            {/* Subsections */}
            {expandedSections.has(section.id) && (
              <div className="ml-4">
                {section.subsections.map((subsection) => {
                  const isActive = currentSection === subsection.id;
                  const completed = isCompleted(subsection.id);
                  
                  return (
                    <button
                      key={subsection.id}
                      onClick={() => onSelectSection(subsection.id)}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                        isActive 
                          ? 'bg-apple-blue text-white' 
                          : 'hover:bg-apple-gray text-apple-text-secondary'
                      }`}
                    >
                      {completed ? (
                        <CheckCircle2 className={`w-4 h-4 ${isActive ? 'text-white' : 'text-apple-green'}`} />
                      ) : (
                        <Circle className={`w-4 h-4 ${isActive ? 'text-white' : 'text-apple-text-secondary'}`} />
                      )}
                      <span className={`text-sm ${isActive ? 'text-white' : ''}`}>
                        {subsection.title}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
